import { ALL_PARTNERS } from "../partners";
import { domainMap } from "./domainMap";
import {
  partnerConfigSchema,
  type PartnerConfig,
} from "./schema";

export type { PartnerConfig };

// Adding a new partner (call it `acme`):
//
//   1. Create src/partners/acme/Logo.tsx — default-export the SVG React
//      component. The schema requires logo.Component to be a function, so
//      forgetting to import or attach the Logo fails validation at boot.
//   2. Create src/partners/acme/config.ts — default-export the config:
//        import Logo from './Logo';
//        const acme = { partnerId: 'acme', ..., logo: { alt: 'Acme', Component: Logo }, ... }
//          satisfies PartnerConfigInput;
//        export default acme;
//      Rules:
//      - partnerId must match /^[a-z][a-z0-9_-]*$/ and be unique.
//      - payments must enable at least one of creditCard/paypal.
//      - branches.sbNames is matched against BE response (strict allowlist).
//      - mailboxes.countries uses BE country names verbatim ('Germany', not 'DE').
//      - signup.mode='open' ships a multi-use token in the bundle —
//        see comment in schema.ts for the threat model.
//   3. Add the import to src/partners/index.ts (kept explicit so the bundle
//      manifest is readable — Jest, webpack, and future bundlers all see the
//      same list).
//   4. Add every production hostname to src/config/domainMap.json. Unmapped
//      hostnames silently fall back to DEFAULT_PARTNER_ID — always test the
//      real host.
//   5. Create public/partners/acme/ with exactly these 10 assets (server.js
//      auto-discovers from the default partner's directory):
//        favicon.ico, favicon.svg, favicon-16x16.png, favicon-32x32.png,
//        apple-touch-icon.png, android-chrome-192x192.png,
//        android-chrome-512x512.png, logo.svg, manifest.json,
//        og-image.png (1200x630).
//   6. External: Intercom workspace (if chat.provider!='none'), DNS/Heroku
//      domain pointing at the existing app. No separate Sentry project —
//      partner is tagged on the shared DSN via sentry.tags.
//   7. Verify: `npx tsc --noEmit && CI=true npm test && npm run test:smoke`.
//      Manually open /?partner=acme in dev. Hit the real hostname in
//      staging (?partner= override is non-prod only).

const RAW_PARTNERS = ALL_PARTNERS;

// Validate at module load. A broken partner config (Zod failure or missing
// Logo Component) is logged and SKIPPED — boot survives so other partners
// and the default fallback keep working. The only unrecoverable case is the
// default partner itself failing (checked below).
const PARTNERS: Record<string, PartnerConfig> = Object.fromEntries(
  RAW_PARTNERS.flatMap((c): Array<[string, PartnerConfig]> => {
    try {
      const validated = partnerConfigSchema.parse(c);
      return [[validated.partnerId, validated]];
    } catch (err) {
      const id =
        (c as { partnerId?: string } | null | undefined)?.partnerId ??
        "<unknown>";
      // eslint-disable-next-line no-console
      console.error(
        `[config] partner "${id}" failed validation — skipping. Users on its domain will fall back to the default partner.`,
        err,
      );
      return [];
    }
  }),
);

const DEFAULT_PARTNER_ID = "sabo";

// Warn (don't throw) on domainMap inconsistencies — a skipped partner above
// will surface here, but the fallback in resolvePartner keeps users alive.
for (const [host, partnerId] of Object.entries(domainMap)) {
  if (!PARTNERS[partnerId]) {
    // eslint-disable-next-line no-console
    console.warn(
      `[config] domainMap host "${host}" maps to "${partnerId}" which is not loaded (typo or skipped partner). Users on "${host}" will fall back to "${DEFAULT_PARTNER_ID}".`,
    );
  }
}

// Hard crash only if the default partner itself didn't load — there's no
// safer partner to degrade to in that case.
if (!PARTNERS[DEFAULT_PARTNER_ID]) {
  throw new Error(
    `[config] DEFAULT_PARTNER_ID "${DEFAULT_PARTNER_ID}" has no matching partner config — cannot boot`,
  );
}

// Look up a partner by id. Returns the default partner if the id is unknown
// (same kill-switch as resolvePartner). Useful for code that already knows the
// partnerId (e.g. selectors reading it from Redux state) and shouldn't have to
// touch window.location.hostname.
export const getPartnerById = (partnerId: string): PartnerConfig =>
  PARTNERS[partnerId] ?? PARTNERS[DEFAULT_PARTNER_ID];

// Resolves the partner config for a given hostname. Behavior (in order):
//  1. In non-production builds, ?partner=<id> overrides everything —
//     useful for local testing without /etc/hosts edits.
//  2. window.__PARTNER__ — injected by server.js into the SPA shell. This
//     is the production source of truth, so the client agrees with the
//     server even if the bundled domainMap and the server's copy drift.
//  3. Hostname lookup in the bundled domainMap. Fallback for `npm run dev`
//     (CRA dev server doesn't run server.js, so no injection) and for any
//     environment where the placeholder wasn't replaced.
//  4. Unknown hostnames or missing partners fall back to DEFAULT_PARTNER_ID
//     (kill-switch: a corrupt config or unmapped domain degrades to SABO
//     instead of producing a white screen).
export const resolvePartner = (hostname: string): PartnerConfig => {
  if (process.env.NODE_ENV !== "production") {
    try {
      const params = new URLSearchParams(window.location.search);
      const override = params.get("partner");
      if (override && PARTNERS[override]) return PARTNERS[override];
    } catch {
      // window.location unavailable (e.g. node test runner without jsdom) —
      // fall through.
    }
  }
  try {
    const injected = (globalThis as { __PARTNER__?: unknown }).__PARTNER__;
    if (typeof injected === "string" && PARTNERS[injected]) {
      return PARTNERS[injected];
    }
  } catch {
    // no globalThis (impossible in supported runtimes) — fall through.
  }
  const partnerId = domainMap[hostname] ?? DEFAULT_PARTNER_ID;
  return PARTNERS[partnerId] ?? PARTNERS[DEFAULT_PARTNER_ID];
};
